import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { AdvancedFilterPicker } from "./AdvancedFilterPicker";
import type {
  AdvancedFilterDefinition,
  AdvancedFilterOption,
  AdvancedFilterValue,
} from "./data-table-advanced-filter.types";
import {
  isActiveAdvancedFilterValue,
  resolveAdvancedFilterDisplayValue,
} from "./data-table-filter-params";

interface ActiveFilterBadgesProps {
  definitions: AdvancedFilterDefinition[];
  activeFilters: Record<string, AdvancedFilterValue>;
  loadedOptions: Record<string, AdvancedFilterOption[]>;
  /** Whether the table state differs from the page's default filters. */
  hasActiveFilters: boolean;
  onApply: (key: string, value: AdvancedFilterValue) => void;
  onRemove: (key: string) => void;
  onReset: () => void;
}

export function ActiveFilterBadges({
  definitions,
  activeFilters,
  loadedOptions,
  hasActiveFilters,
  onApply,
  onRemove,
  onReset,
}: ActiveFilterBadgesProps) {
  if (definitions.length === 0) return null;

  const activeEntries = definitions.filter((definition) =>
    isActiveAdvancedFilterValue(activeFilters[definition.key]),
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <AdvancedFilterPicker
        definitions={definitions}
        activeFilters={activeFilters}
        loadedOptions={loadedOptions}
        onApply={onApply}
      />

      {activeEntries.map((definition) => (
        <Badge
          key={definition.key}
          variant="outline"
          className="gap-1.5 border-border bg-muted py-1 pl-2.5 pr-1.5 text-foreground"
        >
          {definition.label}:{" "}
          {resolveAdvancedFilterDisplayValue(
            definition,
            activeFilters[definition.key],
            loadedOptions,
          )}
          <button
            type="button"
            onClick={() => onRemove(definition.key)}
            className="rounded-full p-0.5 hover:bg-muted-foreground/20"
            aria-label={`Remover filtro ${definition.label}`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}

      {hasActiveFilters && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-foreground"
          onClick={onReset}
        >
          Limpar
        </Button>
      )}
    </div>
  );
}
